/**
 * Level generation: creating new puzzles automatically ("procedurally").
 *
 * The hard requirement is that every level must be winnable. Generating random
 * blocks and hoping is not good enough, so this file works "solution first":
 *
 * 1. Make the grid of colored cells (random, or given, such as from an image).
 * 2. Play the level out on a scratch copy of the grid, one step at a time,
 *    inventing each block as it's needed. Each block's count is set to exactly
 *    the number of cells it actually cleared, so the blocks fit the grid
 *    perfectly. The order of steps is recorded: that's the solution.
 * 3. Put the blocks into columns so that each one is at the front of its
 *    column at the moment the solution needs it.
 * 4. Double-check: replay the recorded solution using the real game rules
 *    (state.ts). The level is only used if the replay wins.
 *
 * Step 4 is a safety net. If the planner and the rules ever disagree (a bug),
 * generation fails loudly instead of handing a player an impossible level.
 *
 * To make levels harder, the planner can also make the solution use the tray
 * on purpose: "parking" a block whose color can't be reached yet, "two-trip"
 * blocks that need two laps, and "mixing" blocks out of order so the player
 * has to dig. The options below control how often each happens.
 */
import { mulberry32 } from "./random";
import {
	type Block,
	type Cell,
	type Color,
	createState,
	DEFAULT_SPEED,
	exposedCounts,
	type GameState,
	type Grid,
	launch,
	launchFrom,
	MAX_RUNNERS,
	type Source,
	TRAY_SIZE,
	update,
} from "./state";
import { trackLength } from "./track";

export { mulberry32 };

/**
 * Every setting that shapes a generated level. The same options, including
 * the same `seed`, always produce exactly the same level.
 */
export interface LevelOptions {
	/** Grid width and height, in cells. */
	width: number;
	height: number;
	/**
	 * How many different colors the random grid uses (the first N palette
	 * colors). Ignored when the grid is given, as with an image.
	 */
	colors: number;
	/** How many block columns go below the grid. */
	columns: number;
	/**
	 * The most cells any one block may clear. Lower values mean more blocks with
	 * smaller counts, which means more decisions for the player.
	 */
	maxCount: number;
	/**
	 * How much to clump colors together. 0 is random noise; each extra pass makes
	 * bigger single-colored patches. Patches hide colors behind other colors,
	 * which makes the order of launches matter more.
	 */
	clustering: number;
	/**
	 * Chance (0 to 1) that, when it's possible, the solution "parks" a block: it
	 * launches a block whose color can't be reached yet, so the block does a lap
	 * clearing nothing and waits in the tray until later. Parking is the main
	 * source of difficulty, because it forces players to plan tray space.
	 */
	parking: number;
	/**
	 * Chance (0 to 1) that, when it's possible, the solution uses a "two-trip"
	 * block: one whose count is bigger than it can use in one lap. It clears what
	 * it can, waits in the tray, then finishes its job on a second lap.
	 */
	twoTrip: number;
	/**
	 * How many blocks to deliberately put out of order in their columns, in
	 * front of blocks the solution needs earlier. The player has to "dig" them
	 * out first. (Often fewer are possible; see `mix` below.)
	 */
	mixing: number;
	/** How many linked groups to create (fewer if they don't fit). */
	links: number;
	/** How many blocks are in each linked group. They sit side by side in neighbouring columns. */
	linkSize: number;
	/** Number of tray slots. */
	traySize: number;
	/** The most blocks allowed on the track at once. */
	maxRunners: number;
	/**
	 * How fast blocks move, in track slots per second, on grids up to 10x10.
	 * Bigger grids go faster in proportion (see `scaledSpeed`).
	 */
	speed: number;
	/**
	 * The seed for the random number generator (see random.ts). Change it to get
	 * a different level with the same settings.
	 */
	seed: number;
}

/**
 * The largest grid the game supports, in cells on each side. Bigger grids
 * make icons too small to read and levels slow to generate (about 2.5 seconds
 * at 64x64). The dev panel's size settings and image conversion use this limit.
 */
export const MAX_GRID_SIZE = 64;

/** The highest number any block can have, whatever the grid size. */
export const MAX_BLOCK_COUNT = 50;

/**
 * A sensible block count cap (`maxCount`) for a grid of this size. A block
 * passes every row and column once per lap, so on a bigger grid it can
 * usefully clear more cells; the cap grows with the grid (about three
 * quarters of width + height), up to `MAX_BLOCK_COUNT`. For example: 12 for
 * 8x8, 24 for 16x16, 48 for 32x32, and 50 for anything bigger.
 */
export function suggestedMaxCount(width: number, height: number): number {
	return Math.min(
		MAX_BLOCK_COUNT,
		Math.max(3, Math.round((width + height) * 0.75)),
	);
}

/**
 * The actual track speed (slots per second) for a level's `speed` setting on
 * a grid this size. Bigger grids have longer tracks, so at a fixed speed a lap
 * would drag on (a 64x64 lap is about six times longer than a 10x10 one).
 * Above 10x10, speed grows in proportion to the track's length, so a lap
 * takes the same time as on a 10x10 grid. Smaller grids are left as they are.
 */
export function scaledSpeed(
	speed: number,
	width: number,
	height: number,
): number {
	const factor = trackLength(width, height) / trackLength(10, 10);
	return speed * Math.max(1, factor);
}

/**
 * How many track slots apart linked blocks travel on a grid this size (see
 * `GameState.trainGap`). The track keeps a minimum thickness (see layout.ts),
 * so on big grids a block on it spans several slots. This gap keeps linked
 * blocks from overlapping even on a phone-sized screen: 1 up to 11 cells
 * across, 2 for 16, 3 for 32, 6 for 64.
 */
export function suggestedTrainGap(width: number, height: number): number {
	return Math.max(1, Math.ceil(Math.max(width, height) / 11));
}

/** Settings for a medium-difficulty level. Also the starting point for custom levels in the dev panel. */
export const DEFAULT_LEVEL: LevelOptions = {
	width: 8,
	height: 8,
	colors: 4,
	columns: 4,
	maxCount: suggestedMaxCount(8, 8),
	clustering: 0,
	parking: 0.3,
	mixing: 2,
	twoTrip: 0.2,
	links: 2,
	linkSize: 2,
	traySize: TRAY_SIZE,
	maxRunners: MAX_RUNNERS,
	speed: DEFAULT_SPEED,
	seed: 1,
};

/** A generated level, plus the steps that win it. */
export interface GeneratedLevel {
	state: GameState;
	/**
	 * The winning sequence of launches. Each launch is made after the previous
	 * block has left the track. Tests and the generator itself use this to prove
	 * the level can be won.
	 */
	solution: Source[];
	/** How many blocks actually ended up out of order (see `mixing`). */
	mixed: number;
}

/** Generate a level and keep only the playable state, dropping the solution. */
export function generateLevel(
	options: LevelOptions = DEFAULT_LEVEL,
	cells?: Cell[],
): GameState {
	return generateLevelWithSolution(options, cells).state;
}

/**
 * Generate a level and a winning solution for it.
 *
 * By default the grid is random (using `colors` and `clustering`). Pass a
 * `fixed` grid (a flat, row-by-row array, with `null` for empty cells) to
 * build the level around a specific picture instead, such as one converted
 * from an image. Throws an error if the grid has no cells.
 */
export function generateLevelWithSolution(
	options: LevelOptions = DEFAULT_LEVEL,
	fixed?: Cell[],
): GeneratedLevel {
	const { width, height, colors, seed } = options;
	const random = mulberry32(seed);
	const pick = (): Color => Math.floor(random() * colors);
	let cells: Cell[];
	if (fixed) {
		if (fixed.length !== width * height) {
			throw new Error(
				`Grid has ${fixed.length} cells; expected ${width}x${height}`,
			);
		}
		if (fixed.every((c) => c === null))
			throw new Error("Grid has no cells to clear");
		cells = [...fixed];
	} else {
		cells = clump(
			Array.from({ length: width * height }, pick),
			width,
			height,
			options.clustering,
			random,
		);
	}

	const { columns, solution, mixed } = new Planner(
		{ width, height, cells: [...cells] },
		options,
		random,
	).plan();
	const make = () =>
		createState(
			{ width, height, cells: [...cells] },
			structuredClone(columns),
			{
				speed: scaledSpeed(options.speed, width, height),
				traySize: options.traySize,
				maxRunners: options.maxRunners,
				trainGap: suggestedTrainGap(width, height),
			},
		);

	if (!replay(make(), solution)) {
		// This should never happen: it means the planner and the game rules
		// disagree. Failing loudly is better than giving a player an impossible level.
		throw new Error(`Generated level failed its own solution (seed ${seed})`);
	}
	return { state: make(), solution, mixed };
}

/**
 * The "planner": plays a grid out step by step, inventing blocks as it goes.
 *
 * Each step is one of:
 * - launch a new block (or linked group) of a color that's reachable right
 *   now; its count becomes exactly what it clears in one lap;
 * - park a new block whose color is still buried; it clears nothing and waits
 *   in the tray;
 * - dig a "two-trip" block early: it clears some cells now and waits in the
 *   tray to finish later;
 * - relaunch a waiting block once its color can be reached.
 *
 * Waiting blocks must always be able to finish later, otherwise the level
 * would be unwinnable. Three rules (called "invariants": things that must
 * always stay true) guarantee it:
 * 1. while a block of some color is waiting, no new block uses that color;
 * 2. that color always keeps at least one cell per waiting block;
 * 3. a relaunched block is limited so it leaves enough cells for the others.
 *
 * Every launch is recorded in a timeline (`ops`), together with a snapshot of
 * the grid just before it. `mix` uses those snapshots afterwards to rearrange
 * blocks safely.
 */
class Planner {
	private readonly columns: Block[][];
	private readonly ops: Op[] = [];
	/**
	 * The planner's own model of the tray: blocks currently waiting, in order.
	 * It mirrors what the real tray will contain when the solution is replayed.
	 */
	private readonly tray: Block[] = [];
	/** How many cells each waiting block cleared on its first lap (0 if it was parked). */
	private readonly firstTrip = new Map<Block, number>();
	/** A copy of the grid taken at the start of the current step, saved with each launch. */
	private before: Cell[] = [];
	private linksLeft: number;
	private nextLink = 0;

	constructor(
		private readonly grid: Grid,
		private readonly options: LevelOptions,
		private readonly random: () => number,
	) {
		this.columns = Array.from({ length: options.columns }, () => []);
		const { linkSize, maxRunners, links } = options;
		this.linksLeft = linkSize >= 2 && linkSize <= maxRunners ? links : 0;
	}

	/**
	 * Run the planner: play steps until the grid is empty, then mix some blocks
	 * out of order. Returns the filled columns, the winning launch sequence, and
	 * how many blocks were mixed.
	 */
	plan(): { columns: Block[][]; solution: Source[]; mixed: number } {
		while (this.grid.cells.some((c) => c !== null)) {
			this.before = [...this.grid.cells];
			this.step();
		}
		const mixed = mix(
			this.columns,
			this.ops,
			this.grid,
			this.options,
			this.random,
		);
		return { columns: this.columns, solution: toSolution(this.ops), mixed };
	}

	/**
	 * Decide and carry out one step of the solution. In priority order: maybe
	 * relaunch a waiting block, maybe park one, maybe dig a two-trip block, maybe
	 * launch a linked group, and otherwise launch a plain block.
	 */
	private step(): void {
		const { random, options } = this;
		const exposed = exposedCounts(this.grid);
		const reserved = new Set(this.tray.map((b) => b.color));

		// If a waiting block's color can be reached again, usually relaunch it now.
		// If every reachable color is reserved by waiting blocks, it must be relaunched.
		const ready = this.tray.findIndex((b) => exposed.has(b.color));
		const open = [...exposed.keys()].some((c) => !reserved.has(c));
		if (ready !== -1 && (!open || random() < 0.6)) {
			this.relaunch(ready);
			return;
		}

		if (this.tray.length < options.traySize && random() < options.parking) {
			const buried = this.buriedColor(exposed);
			if (buried !== null) {
				this.park(buried);
				return;
			}
		}

		if (
			this.tray.length < options.traySize &&
			random() < options.twoTrip &&
			this.digEarly(reserved)
		)
			return;

		const remaining = this.grid.cells.filter((c) => c !== null).length;
		// Spread linked groups across the level: the fewer cells are left compared
		// with the groups still to place, the more likely one is placed now.
		const stepsLeft = Math.max(
			1,
			remaining / Math.max(1, options.maxCount * 0.6),
		);
		if (
			this.linksLeft > 0 &&
			random() < this.linksLeft / stepsLeft &&
			this.group(reserved)
		)
			return;

		const color = this.pickExposed(reserved);
		const [count] = runLap(this.grid, [color], options.maxCount);
		this.deal([{ color, count: count as number }]);
	}

	/**
	 * Pick a color that still has cells but none of them reachable right now, so
	 * a block of that color can be parked. Returns `null` if there isn't one.
	 */
	private buriedColor(exposed: Map<Color, number>): Color | null {
		const remaining = new Map<Color, number>();
		for (const c of this.grid.cells)
			if (c !== null) remaining.set(c, (remaining.get(c) ?? 0) + 1);
		const candidates = [...remaining].filter(([color, cells]) => {
			const parked = this.tray.filter((b) => b.color === color).length;
			// Only if there's at least one cell left for every waiting block of this color, plus this one.
			return !exposed.has(color) && cells > parked;
		});
		const choice = candidates[Math.floor(this.random() * candidates.length)];
		return choice ? choice[0] : null;
	}

	/**
	 * Park a block of a buried color: it goes around once clearing nothing and
	 * waits in the tray. Its count is filled in when it's relaunched.
	 */
	private park(color: Color): void {
		const block: Block = { color, count: 0 }; // count is set when it's relaunched
		this.deal([block], undefined, true);
		this.tray.push(block);
		this.firstTrip.set(block, 0);
	}

	/**
	 * Plan a two-trip block: it clears what it can now, waits in the tray, and
	 * finishes on a later lap. Its count is the total of both laps. Returns
	 * `false`, changing nothing, if no color works right now.
	 */
	private digEarly(reserved: Set<Color>): boolean {
		const { maxCount } = this.options;
		for (let attempt = 0; attempt < 4; attempt++) {
			const color = this.pickExposed(reserved);
			const trial: Grid = { ...this.grid, cells: [...this.grid.cells] };
			// Simulate with an effectively unlimited count, because the real block will
			// still have count left over at this point and so won't stop early.
			const [cleared] = runLap(trial, [color], this.grid.cells.length) as [
				number,
			];
			const left = trial.cells.filter((c) => c === color).length;
			// Skip this color unless the cap leaves room for a second lap and at least one cell remains for it.
			if (cleared >= maxCount || left < 1) continue;
			this.grid.cells = trial.cells;
			const block: Block = { color, count: 0 }; // count is set when it's relaunched
			this.deal([block], undefined, true, true);
			this.tray.push(block);
			this.firstTrip.set(block, cleared);
			return true;
		}
		return false;
	}

	/**
	 * Relaunch the waiting block at tray position `index`. Its final count is
	 * what it cleared on its first lap plus what it clears now.
	 */
	private relaunch(index: number): void {
		const block = this.tray[index] as Block;
		const cells = this.grid.cells.filter((c) => c === block.color).length;
		const others = this.tray.filter(
			(b) => b !== block && b.color === block.color,
		).length;
		const first = this.firstTrip.get(block) ?? 0;
		// Don't exceed the per-block cap in total, and leave at least one cell for
		// each other waiting block of this color.
		const cap = Math.min(this.options.maxCount - first, cells - others);
		const [count] = runLap(this.grid, [block.color], cap);
		block.count = first + (count as number);
		this.tray.splice(index, 1);
		this.ops.push({ from: "tray", block, before: this.before });
	}

	/** Plan a linked group if one fits right now. Returns `false`, changing nothing, if not. */
	private group(reserved: Set<Color>): boolean {
		const { options } = this;
		const at = this.pickColumn(options.linkSize);
		if (at === null) return false;
		for (let attempt = 0; attempt < 6; attempt++) {
			const colors = Array.from({ length: options.linkSize }, () =>
				this.pickExposed(reserved),
			);
			const trial: Grid = { ...this.grid, cells: [...this.grid.cells] };
			const counts = runLap(trial, colors, options.maxCount);
			if (counts.every((c) => c > 0)) {
				this.grid.cells = trial.cells;
				const link = this.nextLink++;
				this.deal(
					colors.map((color, i) => ({
						color,
						count: counts[i] as number,
						link,
					})),
					at,
				);
				this.linksLeft--;
				return true;
			}
		}
		return false;
	}

	/**
	 * Put a step's block(s) at the bottom of a column (side by side, for linked
	 * groups) and record the launch in the timeline.
	 */
	private deal(
		blocks: Block[],
		at = this.pickColumn(blocks.length) as number,
		parks = false,
		partial = false,
	): void {
		for (const [i, block] of blocks.entries())
			this.columns[at + i]?.push(block);
		this.ops.push({
			from: "column",
			column: at,
			blocks,
			parks,
			partial,
			before: this.before,
		});
	}

	/**
	 * Choose a column to put `width` blocks into. Linked groups need `width`
	 * neighbouring columns of equal height so the blocks sit side by side. Shorter
	 * columns are more likely to be chosen, which keeps the columns even. Returns
	 * `null` if a group doesn't fit anywhere.
	 */
	private pickColumn(width: number): number | null {
		const { columns } = this;
		const tallest = Math.max(...columns.map((c) => c.length));
		const choices: { at: number; weight: number }[] = [];
		for (let at = 0; at + width <= columns.length; at++) {
			const height = columns[at]?.length ?? 0;
			if (columns.slice(at, at + width).some((c) => c.length !== height))
				continue;
			choices.push({ at, weight: (tallest + 1 - height) ** 2 });
		}
		return weighted(
			choices.map((c) => [c.at, c.weight]),
			this.random,
		);
	}

	/**
	 * Pick a color that's reachable now and not reserved by a waiting block.
	 * Colors with more reachable cells are more likely to be picked.
	 */
	private pickExposed(reserved: Set<Color>): Color {
		const open = [...exposedCounts(this.grid)].filter(
			([color]) => !reserved.has(color),
		);
		return weighted(open, this.random) as Color;
	}
}

/**
 * One launch in the planned solution, plus a snapshot of the grid just
 * before it. Either a launch from a column or a relaunch from the tray.
 */
type Op = { before: Cell[] } & (
	| {
			from: "column";
			/** The leftmost column launched from (a linked group spans `blocks.length` columns). */
			column: number;
			blocks: Block[];
			/** `true` if this launch ends with the block waiting in the tray (parked, dug, or on its first trip). */
			parks: boolean;
			/** `true` if it also clears cells before reaching the tray (the first lap of a two-trip block). */
			partial: boolean;
	  }
	| { from: "tray"; block: Block }
);

/**
 * Move a few blocks earlier in their columns, so the column order no longer
 * matches the order the solution uses them in. This is the "mixing" option.
 *
 * Say block B is used at step j. Mixing moves B into the same column just in
 * front of block A, which is used earlier, at step i. The player then has to
 * launch B first to reach A. The solution is updated to match: B is "dug out"
 * just before step i (it goes around once and waits in the tray), then
 * relaunched at step j, where it does exactly what it did before.
 *
 * This is only safe when B's color can't be reached at all just before step
 * i. Otherwise digging B out would clear cells and change the rest of the
 * solution. The tray must also have room for B the whole time it waits.
 * Returns how many blocks were moved.
 */
function mix(
	columns: Block[][],
	ops: Op[],
	grid: Grid,
	options: LevelOptions,
	random: () => number,
): number {
	let moved = 0;
	// Which colors could be reached in each grid snapshot. Snapshots never
	// change, so each is worked out once and remembered ("memoized"); without
	// this, large grids spend minutes recomputing the same answers.
	const reachable = new Map<Cell[], Set<Color>>();
	const reachableIn = (cells: Cell[]) => {
		let colors = reachable.get(cells);
		if (!colors) {
			colors = new Set(exposedCounts({ ...grid, cells }).keys());
			reachable.set(cells, colors);
		}
		return colors;
	};
	// Candidates for B: single blocks (not linked) launched from a column, in a
	// random order. Two-trip blocks are left alone, because their first lap clears
	// cells and so can't be moved.
	const candidates = ops.flatMap((op) =>
		op.from === "column" &&
		op.blocks.length === 1 &&
		!op.partial &&
		op.blocks[0]?.link === undefined
			? [op]
			: [],
	);
	shuffle(candidates, random);

	for (const late of candidates) {
		if (moved >= options.mixing) break;
		const j = ops.indexOf(late);
		const b = late.blocks[0] as Block;
		const { column } = late;
		// Try the latest possible step first: the less time B spends waiting in the
		// tray, the more likely it fits.
		for (let i = j - 1; i >= 0; i--) {
			const target = ops[i] as Op;
			if (target.from !== "column") continue;
			if (
				column < target.column ||
				column >= target.column + target.blocks.length
			)
				continue;
			if (reachableIn(target.before).has(b.color)) continue;

			const dig: Op = {
				from: "column",
				column,
				blocks: [b],
				parks: true,
				partial: false,
				before: target.before,
			};
			// If B was already a parked block, it already has a relaunch later, so it
			// just waits longer. Otherwise it needs a relaunch at step j.
			const atJ: Op[] = late.parks
				? []
				: [{ from: "tray", block: b, before: late.before }];
			const next = [
				...ops.slice(0, i),
				dig,
				...ops.slice(i, j),
				...atJ,
				...ops.slice(j + 1),
			];
			if (!trayFits(next, options.traySize)) continue;

			const a = target.blocks[column - target.column] as Block;
			const stack = columns[column] as Block[];
			stack.splice(stack.indexOf(b), 1);
			stack.splice(stack.indexOf(a), 0, b);
			ops.splice(0, ops.length, ...next);
			moved++;
			break;
		}
	}
	return moved;
}

/**
 * Shuffle an array in place, using the "Fisher-Yates" algorithm: walk
 * backwards, swapping each item with a random earlier one (or itself). Every
 * order is equally likely. It uses the seeded random source, so the shuffle
 * is repeatable.
 */
function shuffle<T>(items: T[], random: () => number): void {
	for (let i = items.length - 1; i > 0; i--) {
		const j = Math.floor(random() * (i + 1));
		[items[i], items[j]] = [items[j] as T, items[i] as T];
	}
}

/** Whether following these steps would ever need more tray slots than there are. */
function trayFits(ops: Op[], traySize: number): boolean {
	let held = 0;
	for (const op of ops) {
		if (op.from === "tray") held--;
		else if (op.parks) held++;
		if (held > traySize) return false;
	}
	return true;
}

/**
 * Turn the timeline into the list of launches the game understands. For
 * relaunches, this works out which tray slot each waiting block will be in at
 * that moment, following the same slide-left behaviour as the real tray.
 */
function toSolution(ops: Op[]): Source[] {
	const tray: Block[] = [];
	return ops.map((op) => {
		if (op.from === "column") {
			if (op.parks) tray.push(...op.blocks);
			return { from: "column", index: op.column };
		}
		const index = tray.indexOf(op.block);
		tray.splice(index, 1);
		return { from: "tray", index };
	});
}

/**
 * Pick one value at random, where each value's chance is proportional to its
 * weight. For example, weights 1 and 3 give the second value a 75% chance.
 * Returns `null` for an empty list.
 */
function weighted<T>(
	entries: (readonly [T, number])[],
	random: () => number,
): T | null {
	const total = entries.reduce((sum, [, w]) => sum + w, 0);
	let roll = random() * total;
	for (const [value, w] of entries) {
		roll -= w;
		if (roll < 0) return value;
	}
	return entries.at(-1)?.[0] ?? null;
}

/**
 * Simulate blocks of these colors going around the track once, together (as
 * a linked train if there is more than one), each allowed up to `cap` clears.
 * This runs the real game rules on a temporary state. It changes `grid`, and
 * returns how many cells each block cleared.
 */
function runLap(grid: Grid, colors: Color[], cap: number): number[] {
	const linked = colors.length > 1;
	const state = createState(
		grid,
		colors.map((color) => [
			linked ? { color, count: cap, link: 0 } : { color, count: cap },
		]),
		{
			speed: 1,
			traySize: colors.length,
			maxRunners: colors.length,
			trainGap: suggestedTrainGap(grid.width, grid.height),
		},
	);
	launch(state, 0);
	const runners = [...state.runners];
	while (state.runners.length > 0 && state.status === "playing")
		update(state, 1);
	return runners.map((r) => cap - r.count);
}

/**
 * Play a solution using the real rules, one launch at a time, waiting for
 * each block to leave the track before the next. Returns `true` if it wins.
 * This is how a level proves it can be won.
 */
export function replay(state: GameState, solution: Source[]): boolean {
	const dt = 1 / state.speed;
	for (const source of solution) {
		if (state.status !== "playing") break;
		if (!launchFrom(state, source)) return false;
		for (
			let guard = 0;
			state.runners.length > 0 && state.status === "playing";
			guard++
		) {
			if (guard > 100_000) return false;
			update(state, dt);
		}
	}
	return state.status === "won";
}

/**
 * Clump colors together. In each pass, every cell takes the most common color
 * among itself and its four neighbours (up, down, left, right), with ties
 * broken at random. This is a simple "majority filter", the same idea used to
 * smooth noise out of images.
 */
function clump(
	cells: Color[],
	width: number,
	height: number,
	passes: number,
	random: () => number,
): Color[] {
	let current = cells;
	for (let pass = 0; pass < passes; pass++) {
		current = current.map((color, i) => {
			const x = i % width;
			const y = Math.floor(i / width);
			const tally = new Map<Color, number>([[color, 1]]);
			for (const [dx, dy] of [
				[1, 0],
				[-1, 0],
				[0, 1],
				[0, -1],
			] as const) {
				const nx = x + dx;
				const ny = y + dy;
				if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
				const n = current[ny * width + nx] as Color;
				tally.set(n, (tally.get(n) ?? 0) + 1);
			}
			const top = Math.max(...tally.values());
			const leaders = [...tally].filter(([, v]) => v === top).map(([c]) => c);
			return leaders[Math.floor(random() * leaders.length)] as Color;
		});
	}
	return current;
}
